import { useMutation } from '@tanstack/react-query';

import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useListService from '@/data/hook/useListService';
import {
  createStudentReport,
  StudentReportPayload,
  UpdateReport,
  updateStudentReport,
} from '@/data/services/scheduledLessonReportServices';
import alert from '../../utils/shared/alert';
import { getErrorMessage } from '../../utils/getErrorMessage';
import { scheduledLessonReportsQueryKeys } from '@/data/services/querykeys';
import { useTranslation } from 'react-i18next';

type useStudentReportProps = {
  studentId?: number;
  scheduledLessonId?: number;
};

export default function useStudentReport({
  studentId,
  scheduledLessonId,
}: useStudentReportProps = {}) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const {
    results: reports,
    isInitialLoading: loadingReport,
    invalidate: invalidateReports,
    error: reportError,
  } = useListService({
    ...scheduledLessonReportsQueryKeys.list({
      scheduledLesson: scheduledLessonId,
      student: studentId,
    }),
    enabled: !!scheduledLessonId && !!studentId,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const report = reports.at(0);

  const {
    mutateAsync: createAsync,
    isLoading: loadingCreate,
    error: createError,
  } = useMutation(createStudentReport, {
    async onSuccess() {
      await invalidateReports();
    },

    onError(error: any) {
      alert.error(getErrorMessage(error));
    },
  });

  const {
    mutateAsync: updateAsync,
    isLoading: loadingUpdate,
    error: updateError,
  } = useMutation(updateStudentReport, {
    async onSuccess() {
      alert.success(t('saved'));
      await invalidateReports();
    },

    onError(error: any) {
      alert.error(getErrorMessage(error));
    },
  });

  const updatePromise = async (params: UpdateReport) => {
    try {
      return await updateAsync(params);
    } catch (e) {
      alert.error(getErrorMessage(e));
    }
  };

  const createPromise = async (params: StudentReportPayload) => {
    try {
      return await createAsync(params);
    } catch (e) {
      alert.error(getErrorMessage(e));
    }
  };

  return {
    report,
    loadingReport,
    create: createPromise,
    update: updatePromise,
    invalidateReports,
    loadingCreate,
    loadingUpdate,
    createError,
    updateError,
    reportError,
  };
}
